import { Box, Divider, IconButton, Tooltip } from '@mui/material';
import { Columns2, Columns3, Rows2, Rows3, Square } from 'lucide-react';
import React from 'react';
import { applyLayoutPreset } from './layoutPresets';

/**
 * AnalysisToolbar — global layout controls above the dockview area.
 *
 * Operates on ALL currently open files at once, redistributing them across
 * 1, 2, or 3 groups. Per-group actions (split this group, popout this tab)
 *
 * Props:
 *   getApi: () => DockviewApi | null   — accessor for the dockview API
 *   panelCount: number                  — number of currently open files,
 *                                         used to disable split buttons when
 *                                         there's nothing meaningful to split
 */
const AnalysisToolbar = ({ getApi, panelCount }) => {
  const handlePreset = (preset) => () => {
    const api = getApi?.();
    if (!api) return;
    applyLayoutPreset(api, preset);
  };

  // Reset is always available when at least 2 panels exist (single panel
  // is already "reset"). Splits require at least 2 panels for 2-up, etc.
  const canReset = panelCount >= 2;
  const canSplit2 = panelCount >= 2;
  const canSplit3 = panelCount >= 3;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 0.5,
        px: 1,
        height: 36,
        bgcolor: '#D8DCDF',
        borderBottom: '1px solid #C4CDD3',
        flexShrink: 0,
      }}
    >
      <PresetButton
        title="Combine all into one group"
        Icon={Square}
        disabled={!canReset}
        onClick={handlePreset('reset')}
      />
      <PresetButton
        title="Split into 2 columns"
        Icon={Columns2}
        disabled={!canSplit2}
        onClick={handlePreset('cols-2')}
      />
      <PresetButton
        title="Split into 2 rows"
        Icon={Rows2}
        disabled={!canSplit2}
        onClick={handlePreset('rows-2')}
      />
      <PresetButton
        title="Split into 3 rows"
        Icon={Rows3}
        disabled={!canSplit3}
        onClick={handlePreset('rows-3')}
      />
      <PresetButton
        title="Split into 3 columns"
        Icon={Columns3}
        disabled={!canSplit3}
        onClick={handlePreset('cols-3')}
      />
    </Box>
  );
};

const PresetButton = ({ title, Icon, disabled, onClick }) => (
  <Tooltip title={title} arrow disableInteractive>
    {/* span wrapper required because Tooltip doesn't fire on disabled buttons */}
    <span>
      <IconButton
        size="small"
        disabled={disabled}
        onClick={onClick}
        sx={{
          width: 28,
          height: 28,
          borderRadius: 1,
          color: '#394b57',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' },
          '&.Mui-disabled': { color: '#C4CDD3' },
        }}
      >
        <Icon size={16} strokeWidth={1.75} />
      </IconButton>
    </span>
  </Tooltip>
);

export default AnalysisToolbar;
